import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Activity, Database, Bell, Layers, Package, LogOut, User, Map as MapIcon, QrCode, BarChart3, BellRing, Settings, Radio, ScanBarcode, KeyRound, Loader2, Building2, Users } from 'lucide-react';
import { LogoIcon } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Change Password state
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  const handleChangePassword = async () => {
    setPwdError('');
    setPwdSuccess('');
    if (newPwd !== confirmPwd) {
      setPwdError('New passwords do not match');
      return;
    }
    if (newPwd.length < 6) {
      setPwdError('New password must be at least 6 characters');
      return;
    }
    setPwdLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/auth/change-password`, {
        current_password: currentPwd,
        new_password: newPwd,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setPwdSuccess('Password changed successfully');
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
      setTimeout(() => setPwdOpen(false), 1500);
    } catch (err) {
      setPwdError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwdLoading(false);
    }
  };

  // Role-based navigation based on new RBAC
  const getNavItems = () => {
    const commonItems = [
      { path: '/app/dashboard', label: 'Dashboard', icon: Activity },
      { path: '/app/shipments', label: 'Shipments', icon: Package },
      { path: '/app/map', label: 'Map', icon: MapIcon },
    ];

    // Superadmin sees everything
    if (user?.role === 'superadmin') {
      return [
        ...commonItems,
        { path: '/app/devices', label: 'Devices', icon: Layers },
        { path: '/app/connectors', label: 'Connectors', icon: Database },
        { path: '/app/alerts', label: 'Alerts', icon: Bell },
        { path: '/app/alert-rules', label: 'Rules', icon: BellRing },
        { path: '/app/analytics', label: 'Analytics', icon: BarChart3 },
        { path: '/app/silica', label: 'SILICA', icon: Radio },
        { path: '/app/bulk-activate', label: 'Activate', icon: ScanBarcode },
        { path: '/app/receivers', label: 'Partners', icon: Building2 },
        { path: '/app/users', label: 'Users', icon: Users },
        { path: '/app/scanner', label: 'Scanner', icon: QrCode },
      ];
    }

    // Pallet Pool users
    if (user?.organization_type === 'pallet_pool') {
      const items = [...commonItems];
      
      // Owner - focused view: their devices, activate, receivers
      if (user?.role === 'owner') {
        items.push(
          { path: '/app/devices', label: 'Devices', icon: Layers },
          { path: '/app/silica', label: 'IoT Data', icon: Radio },
          { path: '/app/bulk-activate', label: 'Activate', icon: ScanBarcode },
          { path: '/app/receivers', label: 'Partners', icon: Building2 },
        );
      // Admin and Finance see devices and analytics
      } else if (user?.role === 'admin') {
        items.push(
          { path: '/app/devices', label: 'Devices', icon: Layers },
          { path: '/app/bulk-activate', label: 'Activate', icon: ScanBarcode },
          { path: '/app/connectors', label: 'Connectors', icon: Database },
          { path: '/app/alerts', label: 'Alerts', icon: Bell },
          { path: '/app/alert-rules', label: 'Rules', icon: BellRing },
          { path: '/app/analytics', label: 'Analytics', icon: BarChart3 },
          { path: '/app/scanner', label: 'Scanner', icon: QrCode },
        );
      } else if (user?.role === 'finance') {
        items.push(
          { path: '/app/analytics', label: 'Analytics', icon: BarChart3 },
        );
      }
      // View role only sees common items
      return items;
    }

    // Customer users
    if (user?.organization_type === 'customer') {
      const items = [...commonItems, { path: '/app/alerts', label: 'Alerts', icon: Bell }];
      
      if (user?.role === 'admin') {
        items.push({ path: '/app/analytics', label: 'Analytics', icon: BarChart3 });
      }
      return items;
    }

    // Support Team - troubleshooting, firmware, logs
    if (user?.role === 'support') {
      return [
        ...commonItems,
        { path: '/app/devices', label: 'Devices', icon: Layers },
        { path: '/app/support', label: 'Support Tools', icon: Settings },
        { path: '/app/alerts', label: 'Alerts', icon: Bell },
        { path: '/app/analytics', label: 'Analytics', icon: BarChart3 },
      ];
    }

    // Owner - focused view: their devices, shipments, map
    if (user?.role === 'owner') {
      return [
        ...commonItems,
        { path: '/app/devices', label: 'Devices', icon: Layers },
        { path: '/app/bulk-activate', label: 'Activate', icon: ScanBarcode },
      ];
    }

    // API Integrator - query status, view webhooks
    if (user?.role === 'api_integrator') {
      return [
        ...commonItems,
        { path: '/app/support', label: 'API & Webhooks', icon: Database },
      ];
    }

    // Transporter (legacy)
    if (user?.organization_type === 'transporter') {
      return [
        ...commonItems,
        { path: '/app/scanner', label: 'Scanner', icon: QrCode },
      ];
    }

    return commonItems;
  };

  const navItems = getNavItems();

  const getRoleBadgeColor = () => {
    if (user?.role === 'superadmin') return 'bg-red-100 text-red-700';
    
    switch (user?.organization_type) {
      case 'pallet_pool':
        return 'bg-blue-100 text-blue-700';
      case 'transporter':
        return 'bg-green-100 text-green-700';
      case 'customer':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const getRoleLabel = () => {
    if (user?.role === 'superadmin') return 'Superadmin';
    
    const orgType = user?.organization_type === 'pallet_pool' ? 'Pallet Pool' :
                    user?.organization_type === 'customer' ? 'Customer' :
                    user?.organization_type === 'transporter' ? 'Transporter' : '';
    
    const roleLabel = user?.role === 'admin' ? 'Admin' :
                      user?.role === 'finance' ? 'Finance' :
                      user?.role === 'view' ? 'View' : user?.role;
    
    return `${orgType} ${roleLabel}`.trim();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-100/50 to-cyan-100/30">
      {/* Glassmorphism Header */}
      <header className="bg-white/70 backdrop-blur-xl border-b border-white/30 shadow-lg shadow-blue-500/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <LogoIcon size={40} />
              <div>
                <h1 className="text-xl font-bold text-slate-900">SmartPallet</h1>
                <p className="text-xs text-slate-500">IoT Asset Tracking</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Glassmorphism Nav */}
              <nav className="flex items-center gap-1 bg-white/40 backdrop-blur-md rounded-xl p-1 border border-white/30">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          isActive
                            ? 'bg-gradient-to-r from-blue-500/90 to-cyan-500/90 text-white shadow-md'
                            : 'text-slate-600 hover:bg-white/60 hover:text-slate-900'
                        }`
                      }
                    >
                      <Icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{item.label}</span>
                    </NavLink>
                  );
                })}
              </nav>

              {/* Glassmorphism User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 bg-white/40 backdrop-blur-md border border-white/30 hover:bg-white/60" data-testid="user-menu-btn">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                      <p className={`text-xs px-2 py-0.5 rounded ${getRoleBadgeColor()}`}>
                        {getRoleLabel()}
                      </p>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white/80 backdrop-blur-xl border border-white/30">
                  <DropdownMenuLabel>
                    <div>
                      <p className="font-medium">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                      <p className="text-xs text-slate-500 mt-1">{user?.organization}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => { setPwdError(''); setPwdSuccess(''); setCurrentPwd(''); setNewPwd(''); setConfirmPwd(''); setPwdOpen(true); }} data-testid="change-password-btn">
                    <KeyRound className="w-4 h-4 mr-2" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Change Password Dialog */}
              <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
                <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
                  <DialogHeader>
                    <DialogTitle>Change Password</DialogTitle>
                    <DialogDescription>Enter your current password and choose a new one.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    {pwdError && <p className="text-sm text-red-600 bg-red-50 p-2 rounded" data-testid="pwd-error">{pwdError}</p>}
                    {pwdSuccess && <p className="text-sm text-green-600 bg-green-50 p-2 rounded" data-testid="pwd-success">{pwdSuccess}</p>}
                    <div className="space-y-1">
                      <Label htmlFor="current-pwd">Current Password</Label>
                      <Input id="current-pwd" type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} data-testid="current-password-input" />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="new-pwd">New Password</Label>
                      <Input id="new-pwd" type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} data-testid="new-password-input" />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="confirm-pwd">Confirm New Password</Label>
                      <Input id="confirm-pwd" type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} data-testid="confirm-password-input" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setPwdOpen(false)} data-testid="pwd-cancel-btn">Cancel</Button>
                    <Button onClick={handleChangePassword} disabled={pwdLoading} data-testid="pwd-submit-btn">
                      {pwdLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Change Password
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
